import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  Users, UserCheck, Briefcase, Home, Heart, GraduationCap,
  LogIn, BookOpen, Target, Sparkles, Check
} from "lucide-react";
import { toast } from "sonner";
import { EvoluraLogo } from "../components/EvoluraLogo";

const API = '/api';

const TOOL_LABELS = {
  swot: "Personal SWOT",
  core_values: "Core Values"
};

const TOOL_ICONS = {
  swot: Target,
  core_values: Heart
};

const InvitePage = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");

  const fetchInvite = useCallback(async () => {
    try {
      const response = await fetch(`${API}/network/invite-info/${token}`);
      if (response.ok) {
        setInvite(await response.json());
      } else {
        setError("This invite link is invalid or has expired.");
      }
    } catch (err) {
      setError("Failed to load invite.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvite();
  }, [fetchInvite]);

  const acceptInvite = async () => {
    const isGeneral = invite.general_invite;
    const needsRole = isGeneral || invite.role_is_placeholder;
    if (needsRole && !selectedRole) {
      toast.error('Please select your relationship');
      return;
    }

    setAccepting(true);
    try {
      const response = await fetch(`${API}/network/invite-info/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: needsRole ? selectedRole : null })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'already_accepted') {
          toast.success('This invite was already accepted!');
          navigate('/dashboard');
        } else {
          toast.success('Connection accepted!');
          // If there's a pending insight request, go straight to Give Insight page
          if (result.insight_request_id) {
            navigate(`/insights/give/${result.insight_request_id}`);
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to accept invite');
      }
    } catch (err) {
      toast.error('Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const getRoleIcon = (role) => {
    const icons = { mentor: Users, mentee: GraduationCap, professor: BookOpen, student: GraduationCap, coworker: Briefcase, coach: Users, coachee: Users, business_partner: Briefcase, therapist: Heart, family: Home, friend: Heart, other: Users };
    return icons[role] || Users;
  };

  const getRoleColor = (role) => {
    const colors = {
      mentor: 'bg-purple-100 text-purple-600',
      mentee: 'bg-indigo-100 text-indigo-600',
      professor: 'bg-teal-100 text-teal-600',
      student: 'bg-cyan-100 text-cyan-600',
      coworker: 'bg-violet-100 text-violet-600',
      coach: 'bg-orange-100 text-orange-600',
      coachee: 'bg-orange-100 text-orange-600',
      business_partner: 'bg-blue-100 text-blue-600',
      therapist: 'bg-pink-100 text-pink-600',
      family: 'bg-green-100 text-green-600',
      friend: 'bg-amber-100 text-amber-600',
      other: 'bg-slate-100 text-slate-500'
    };
    return colors[role] || 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
            Invalid Invite
          </h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full">
            Go to Evoluras
          </Button>
        </div>
      </div>
    );
  }

  const RoleIcon = invite.role ? getRoleIcon(invite.role) : Users;
  const isGeneral = invite.general_invite;
  const needsRole = isGeneral || invite.role_is_placeholder;
  const hasInsightContext = !!invite.insight_tool_type;
  const ToolIcon = hasInsightContext ? (TOOL_ICONS[invite.insight_tool_type] || Sparkles) : null;
  const toolLabel = hasInsightContext ? (TOOL_LABELS[invite.insight_tool_type] || invite.insight_tool_type) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="invite-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <EvoluraLogo size={47} />
        </div>

        {/* Invite Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          {/* Inviter avatar */}
          <Avatar className="h-20 w-20 mx-auto mb-4 ring-4 ring-violet-100" data-testid="inviter-avatar">
            <AvatarImage src={invite.inviter_picture} alt={invite.inviter_name} />
            <AvatarFallback className={`text-2xl ${invite.role ? getRoleColor(invite.role) : 'bg-violet-100 text-violet-600'}`}>
              {invite.inviter_name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          {invite.inviter_username && (
            <a
              href={`/${invite.inviter_username}`}
              className="text-xs text-slate-500 hover:text-violet-600 block -mt-2 mb-3"
              data-testid="inviter-handle"
              title="View their public profile"
            >
              @{invite.inviter_username}
            </a>
          )}

          {invite.status === 'accepted' ? (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
                Already Connected!
              </h1>
              <p className="text-slate-600 mb-6">
                This invite has already been accepted.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full w-full" data-testid="go-dashboard-button">
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
                {hasInsightContext ? "Your Insights are Requested!" : "Someone who knows you thought you belonged here"}
              </h1>
              <p className="text-slate-600 mb-1">
                <span className="font-semibold text-slate-900">{invite.inviter_name}</span>
                {hasInsightContext
                  ? " wants your insights on their"
                  : " wants to connect with you"
                }
              </p>

              {/* Tool context badge */}
              {hasInsightContext && (
                <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 text-sm font-medium px-4 py-1.5 rounded-full mb-3" data-testid="insight-tool-badge">
                  {ToolIcon && <ToolIcon className="w-4 h-4" />}
                  {toolLabel}
                </span>
              )}

              {/* Role badge for non-insight invites */}
              {!hasInsightContext && invite.role && !invite.role_is_placeholder && (
                <span className="inline-block badge bg-violet-100 text-violet-600 capitalize text-sm px-3 py-1 mb-4">
                  as their {invite.role}
                </span>
              )}

              {/* Insight message */}
              {hasInsightContext && invite.insight_message && (
                <p className="text-sm text-slate-500 italic mb-4" data-testid="insight-message">
                  "{invite.insight_message}"
                </p>
              )}

              <p className="text-sm text-slate-500 mb-6">
                {hasInsightContext
                  ? `Join Evoluras to share your perspective and help ${invite.inviter_name} grow.`
                  : "Evoluras is where honest perspectives meet real growth. Your voice matters here."
                }
              </p>

              {user ? (
                <div className="space-y-4">
                  {/* Role picker: shown for general invites OR when a role is a placeholder (e.g. gratitude invite) */}
                  {needsRole && (
                    <div className="text-left">
                      <label className="block text-sm font-semibold text-slate-800 mb-3">
                        How do you know {invite.inviter_name}?
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { value: "mentor", label: "I'm their Mentor", desc: "You guide or advise them", icon: GraduationCap },
                          { value: "mentee", label: "They're my Mentor", desc: "They guide or advise you", icon: GraduationCap },
                          { value: "advisor", label: "I'm their Advisor", desc: "You advise them professionally", icon: BookOpen },
                          { value: "advising", label: "They advise me", desc: "They're your advisor", icon: BookOpen },
                          { value: "professor", label: "I'm their Professor", desc: "You teach them", icon: GraduationCap },
                          { value: "student", label: "I'm their Student", desc: "They teach you", icon: GraduationCap },
                          { value: "coach", label: "I coach them", desc: "You coach them", icon: Target },
                          { value: "coachee", label: "They coach me", desc: "They're your coach", icon: Target },
                          { value: "therapist", label: "I'm their Therapist", desc: "You provide therapy", icon: Heart },
                          { value: "coworker", label: "Coworker", desc: "You work together", icon: Briefcase },
                          { value: "business_partner", label: "Business Partner", desc: "You work on something together", icon: Briefcase },
                          { value: "friend", label: "Friend", desc: "Personal relationship", icon: Heart },
                          { value: "family", label: "Family", desc: "Family member", icon: Home },
                          { value: "other", label: "Other", desc: "Another type of relationship", icon: Users },
                        ].map((opt) => {
                          const Icon = opt.icon;
                          const selected = selectedRole === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setSelectedRole(opt.value)}
                              className={`flex items-center gap-3 w-full text-left px-3.5 py-2.5 rounded-xl border-2 transition-all ${
                                selected
                                  ? "border-violet-500 bg-violet-50"
                                  : "border-slate-200 bg-white hover:border-slate-300"
                              }`}
                              data-testid={`role-option-${opt.value}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                selected ? "bg-violet-100" : "bg-slate-100"
                              }`}>
                                <Icon className={`w-4 h-4 ${selected ? "text-violet-600" : "text-slate-500"}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${selected ? "text-violet-700" : "text-slate-800"}`}>{opt.label}</p>
                                <p className="text-xs text-slate-400">{opt.desc}</p>
                              </div>
                              {selected && (
                                <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={acceptInvite}
                    disabled={accepting || (needsRole && !selectedRole)}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full w-full"
                    data-testid="accept-invite-button"
                  >
                    {accepting ? 'Accepting...' : (
                      <>
                        <UserCheck className="w-4 h-4 mr-2" />
                        {hasInsightContext ? "Accept & Give Insights" : "Accept & Connect"}
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">Sign in to accept this invite</p>
                  <Button
                    onClick={() => {
                      sessionStorage.setItem('pending_invite_token', token);
                      window.location.href = `/api/auth/google/login?invite_token=${token}`;
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full w-full"
                    data-testid="signin-to-accept-button"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In to Accept
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
